import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../utils/currency';

function ViewProductModal({ isOpen, onClose, product }) {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Handle product images - could be array or single string
  const productImages = useMemo(() => {
    return product?.images && Array.isArray(product.images) 
      ? product.images 
      : product?.image 
      ? [product.image] 
      : [];
  }, [product?.images, product?.image]);

  // Handle product videos - could be array or single string
  const productVideos = useMemo(() => {
    return product?.videos && Array.isArray(product.videos)
      ? product.videos
      : product?.video
      ? [product.video]
      : [];
  }, [product?.videos, product?.video]);

  // Combine images and videos into a single media array (memoized)
  const allMedia = useMemo(() => [
    ...productImages.map(url => ({ type: 'image', url })),
    ...productVideos.map(url => ({ type: 'video', url }))
  ], [productImages, productVideos]);

  const hasMultipleMedia = allMedia.length > 1;

  // Reset media index when product changes
  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [product?.id]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Keyboard navigation for media slider
  useEffect(() => {
    if (!isOpen || !product || allMedia.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentMediaIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentMediaIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, product, allMedia]);

  if (!isOpen || !product) return null;

  const currentMedia = allMedia[currentMediaIndex] || allMedia[0] || null;

  const goToPrevious = () => {
    setCurrentMediaIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentMediaIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const goToMedia = (index) => {
    setCurrentMediaIndex(index);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-3 lg:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-lg md:rounded-xl lg:rounded-2xl shadow-2xl max-w-full md:max-w-3xl lg:max-w-4xl xl:max-w-5xl w-full max-h-[98vh] md:max-h-[95vh] lg:max-h-[90vh] overflow-hidden flex flex-col animate-modal-pop my-2 md:my-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-3 md:px-4 lg:px-6 xl:px-8 py-2.5 md:py-3 lg:py-4 xl:py-5 lg:py-6 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-3 lg:gap-4 flex-1 min-w-0">
            <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-white truncate">View Product</h2>
              <p className="text-xs md:text-sm text-white/80 mt-0.5 md:mt-1 hidden md:block">Product Details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 md:p-2 hover:bg-white/20 rounded-lg md:rounded-xl transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 md:p-4 lg:p-6 xl:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 lg:gap-6 xl:gap-8">
            {/* Left Column - Images & Videos */}
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {/* Media Slider - Combined Images and Videos */}
              {allMedia.length > 0 ? (
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                    {productImages.length > 0 && productVideos.length > 0 ? 'Media' : 
                     productImages.length > 0 ? 'Images' : 'Videos'}
                  </h3>
                  <div className="relative rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-200 bg-white">
                    {/* Main Media Display */}
                    <div className="relative w-full bg-gray-100" style={{ aspectRatio: currentMedia?.type === 'video' ? '16 / 9' : '1 / 1', minHeight: '200px' }}>
                      {currentMedia?.type === 'image' ? (
                        <img
                          src={currentMedia.url}
                          alt={`${product.name} ${currentMediaIndex + 1}`}
                          className="w-full h-full object-cover"
                          style={{ display: 'block' }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                          }}
                        />
                      ) : currentMedia?.type === 'video' ? (
                        <video
                          src={currentMedia.url}
                          controls
                          className="w-full h-full object-contain"
                          preload="metadata"
                          onError={(e) => {
                            console.error('Video load error:', currentMedia.url);
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'absolute inset-0 flex items-center justify-center bg-gray-800 text-white p-4 text-center';
                            const errorText = document.createElement('p');
                            errorText.textContent = 'Video could not be loaded';
                            errorDiv.appendChild(errorText);
                            e.target.parentElement.appendChild(errorDiv);
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : null}
                      
                      {/* Navigation Buttons - Only show if multiple media items */}
                      {hasMultipleMedia && (
                        <>
                          <button
                            onClick={goToPrevious}
                            className="absolute left-1 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 z-10"
                            aria-label="Previous media"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={goToNext}
                            className="absolute right-1 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 z-10"
                            aria-label="Next media"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          
                          {/* Media Counter */}
                          <div className="absolute top-1 sm:top-2 md:top-4 right-1 sm:right-2 md:right-4 bg-black/70 text-white px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-semibold z-10">
                            {currentMediaIndex + 1} / {allMedia.length}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Thumbnail Strip - Shows both images and videos (always show if there are any media items) */}
                    {allMedia.length > 0 && (
                      <div className="bg-gray-50 border-t-2 border-gray-200 p-2 sm:p-3 md:p-4">
                        <div className="flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 overflow-x-auto scrollbar-hide pb-2">
                          {allMedia.map((media, index) => (
                            <button
                              key={index}
                              onClick={() => goToMedia(index)}
                              className={`relative flex-shrink-0 rounded-md sm:rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                                index === currentMediaIndex
                                  ? 'border-primary ring-2 ring-primary/30 scale-105 shadow-md'
                                  : 'border-gray-300 hover:border-gray-400 opacity-70 hover:opacity-100'
                              }`}
                              style={{ width: '50px', height: '50px' }}
                              onMouseEnter={(e) => {
                                if (window.innerWidth >= 640) {
                                  e.currentTarget.style.width = '70px';
                                  e.currentTarget.style.height = '70px';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (window.innerWidth >= 640) {
                                  e.currentTarget.style.width = '50px';
                                  e.currentTarget.style.height = '50px';
                                }
                              }}
                            >
                              {media.type === 'image' ? (
                                <img
                                  src={media.url}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/80x80?text=?';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative overflow-hidden">
                                  <video
                                    src={media.url}
                                    className="w-full h-full object-cover"
                                    preload="metadata"
                                    muted
                                    playsInline
                                    onLoadedMetadata={(e) => {
                                      // Try to capture a frame for thumbnail
                                      try {
                                        const video = e.target;
                                        const canvas = document.createElement('canvas');
                                        canvas.width = video.videoWidth || 50;
                                        canvas.height = video.videoHeight || 50;
                                        const ctx = canvas.getContext('2d');
                                        video.currentTime = 0.1; // Seek to first frame
                                        setTimeout(() => {
                                          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                                          const thumbnailUrl = canvas.toDataURL();
                                          if (thumbnailUrl && thumbnailUrl !== 'data:,') {
                                            e.target.parentElement.style.backgroundImage = `url(${thumbnailUrl})`;
                                            e.target.parentElement.style.backgroundSize = 'cover';
                                            e.target.parentElement.style.backgroundPosition = 'center';
                                          }
                                        }, 100);
                                      } catch (err) {
                                        console.error('Error generating video thumbnail:', err);
                                      }
                                    }}
                                    onError={(e) => {
                                      console.error('Video thumbnail error:', media.url);
                                    }}
                                  />
                                  {/* Play icon overlay - always visible for videos */}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors">
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                  {/* Video indicator badge */}
                                  <div className="absolute top-0.5 right-0.5 bg-blue-600 text-white text-[8px] px-1 py-0.5 rounded font-bold">
                                    VID
                                  </div>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 text-center">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-500">No images or videos available</p>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {/* Product Name */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Product Name</label>
                <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{product.name || 'N/A'}</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Description</label>
                <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap break-words">{product.description || 'No description available'}</p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Category</label>
                <span className="inline-block px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-lg font-semibold text-xs sm:text-sm">
                  {product.category?.name || 'Uncategorized'}
                </span>
              </div>

              {/* Colors */}
              {product.colors && Array.isArray(product.colors) && product.colors.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Available Colors</label>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg font-medium text-xs sm:text-sm border border-gray-200"
                      >
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sizes */}
              {product.sizes && Array.isArray(product.sizes) && product.sizes.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Available Sizes</label>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg font-medium text-xs sm:text-sm border border-gray-200"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Cost Price</label>
                    <p className="text-lg sm:text-xl font-bold text-gray-800">{formatCurrency(product.cost_price || 0)}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Selling Price</label>
                    <p className="text-lg sm:text-xl font-bold text-primary">{formatCurrency(product.price)}</p>
                  </div>
                </div>
                {(product.original_price || product.percent_reduction) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 pt-2 border-t border-gray-200">
                    {product.original_price && (
                      <div>
                        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Original Price</label>
                        <p className="text-base sm:text-lg font-semibold text-gray-600 line-through">{formatCurrency(product.original_price)}</p>
                      </div>
                    )}
                    {product.percent_reduction && (
                      <div>
                        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Discount</label>
                        <p className="text-base sm:text-lg font-bold text-emerald-600">{parseFloat(product.percent_reduction).toFixed(1)}% OFF</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stock */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">Stock Level</label>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className={`text-xl sm:text-2xl font-bold ${
                    (product.stock || 0) === 0 ? 'text-red-600' :
                    (product.stock || 0) <= 10 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {product.stock || 0}
                  </span>
                  <span className={`px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-xs font-semibold ${
                    (product.stock || 0) === 0 ? 'bg-red-100 text-red-700' :
                    (product.stock || 0) <= 10 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {(product.stock || 0) === 0 ? 'Out of Stock' :
                     (product.stock || 0) <= 10 ? 'Low Stock' :
                     'In Stock'}
                  </span>
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                <span className={`px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-xs font-semibold ${
                  product.is_active !== false
                    ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-700 border-2 border-green-200/50'
                    : 'bg-gradient-to-r from-red-100 to-red-50 text-red-700 border-2 border-red-200/50'
                }`}>
                  {product.is_active !== false ? 'Active' : 'Inactive'}
                </span>
                {product.is_new && (
                  <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-[10px] sm:text-xs font-semibold border-2 border-blue-200/50">
                    New
                  </span>
                )}
                {product.is_promotional && (
                  <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-[10px] sm:text-xs font-semibold border-2 border-purple-200/50">
                    Promotional
                  </span>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 pt-3 sm:pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1">Created</label>
                  <p className="text-xs sm:text-sm text-gray-700">
                    {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1">Last Updated</label>
                  <p className="text-xs sm:text-sm text-gray-700">
                    {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5 lg:py-6 bg-gray-50 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold hover:from-gray-500 hover:to-gray-600 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewProductModal;

